import { escapeHtml, toast } from "./util.js";
import { formatRecurringSchedule } from "./date-utils.js";
import { cachedGroupDetail, fetchGroupDetail } from "./meta-cache.js";
import { apiFetch } from "./api.js";
import { t } from "./i18n.js";
import type { GroupDetail } from "./api-types.js";

export async function loadSettingsView(state: any, groupId: string): Promise<void> {
  // Stale-while-revalidate, same as the feed: paint instantly from the
  // metadata cache when we have it, refresh in the background. The view is
  // read-only, so a full re-render on fresh data is safe.
  const freshPromise = fetchGroupDetail(groupId);
  const cached = cachedGroupDetail(groupId);
  const view = document.getElementById("settings-view");
  if (!view) return;
  if (cached) {
    renderSettings(view, cached, groupId);
    void freshPromise
      .then((fresh) => {
        // routeMain recreates #settings-view per navigation; a detached
        // element means the user moved on (or to another group's settings).
        if (view.isConnected) renderSettings(view, fresh, groupId);
      })
      .catch(() => { /* stale settings stand */ });
    return;
  }
  renderSettings(view, await freshPromise, groupId);
}

/** Round to the nearest integer, breaking exact .5 ties to the nearest EVEN
 *  integer (banker's rounding) rather than always up.
 *
 *  This makes the conversion STABLE, not lossless. The wire format carries
 *  only a whole hour, so a half-hour-offset timezone (UTC+5:30 etc.) cannot
 *  represent every local hour: in Kolkata, 12 of the 24 local hours — the odd
 *  ones — come back one hour later on the first round trip (local 09:00 → UTC
 *  04:00 → redisplayed 10:00). Banker's rounding does NOT prevent that first
 *  shift; what it buys is that the shifted value is a fixed point, so the
 *  error stops there instead of drifting another hour on every subsequent
 *  save. That fixed-point property is what settings.test.ts pins.
 *
 *  Users in India, Iran, Newfoundland, and parts of Australia are affected.
 *  The real fix is minutes on the wire (publication_minute_utc); until then
 *  the schedule editor warns when the saved slot won't match what was typed —
 *  see slotRoundsAway(). */
function roundHalfToEven(x: number): number {
  const floor = Math.floor(x);
  const diff = x - floor;
  if (diff < 0.5) return floor;
  if (diff > 0.5) return floor + 1;
  return floor % 2 === 0 ? floor : floor + 1;
}

/** Convert a locally-entered (day-of-week 0=Mon..6=Sun, hour 0-23) pair to
 *  UTC, given the browser's timezone offset in minutes (Date.getTimezoneOffset()
 *  semantics: positive = west of UTC, e.g. UTC+1 → -60). Pure so it's testable
 *  without a real Date/timezone. */
export function localDowHourToUtc(
  localDow: number, localHour: number, offsetMinutes: number
): { dow: number; hour: number } {
  const offsetHours = offsetMinutes / 60;
  let utcHour = roundHalfToEven(localHour + offsetHours);
  let dowShift = 0;
  if (utcHour < 0) { utcHour += 24; dowShift = -1; }
  if (utcHour >= 24) { utcHour -= 24; dowShift = 1; }
  const dow = (localDow + dowShift + 7) % 7;
  return { dow, hour: utcHour };
}

/** Inverse: UTC day/hour to local, for pre-filling the edit inputs. */
export function utcDowHourToLocal(
  utcDow: number, utcHour: number, offsetMinutes: number
): { dow: number; hour: number } {
  return localDowHourToUtc(utcDow, utcHour, -offsetMinutes);
}

/** True when saving this local slot would redisplay as a different hour —
 *  i.e. the whole-hour wire format can't hold it (half-hour timezones). Lets
 *  the editor say so up front instead of silently moving the user's choice.
 *  Returns the hour they'd actually get, or null when the slot round-trips. */
export function slotRoundsAway(
  localDow: number, localHour: number, offsetMinutes: number
): number | null {
  const utc = localDowHourToUtc(localDow, localHour, offsetMinutes);
  const back = utcDowHourToLocal(utc.dow, utc.hour, offsetMinutes);
  return back.hour === localHour ? null : back.hour;
}

function renderSettings(view: HTMLElement, data: GroupDetail, groupId: string): void {
  const me = data.members.find((m: any) => m.is_you);
  // Founder inline editors are gated on BOTH founder-ness and active status:
  // the server rejects edits on a non-active group with 409 (patch_group),
  // so a founder viewing a setup-status group must see the read-only view,
  // not Edit buttons that would always fail. See settings-overhaul spec.
  const isFounder = (me?.is_founder ?? false) && data.status === "active";
  const offset = new Date().getTimezoneOffset();

  view.innerHTML = `
    <h2><span class="feed-settings-gear" aria-hidden="true">⚙</span> ${t("app.details", { name: escapeHtml(data.name) })}</h2>
    <div style="margin-top:1.5rem">
      <div class="form-group"><label>${t("details.publishes")}</label>
        <p>${escapeHtml(formatRecurringSchedule(data.next_publication_slot))} <span style="color:var(--muted)">(your time)</span></p>
        ${isFounder ? `<button class="btn-secondary" id="details-edit-schedule">${t("settings.edit")}</button>
          <div id="details-schedule-form" style="display:none;margin-top:.5rem">
            <select id="details-dow">
              ${["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => `<option value="${i}">${d}</option>`).join("")}
            </select>
            <select id="details-hour">
              ${Array.from({ length: 24 }, (_, h) => `<option value="${h}">${String(h).padStart(2, "0")}:00</option>`).join("")}
            </select>
            <button class="btn" id="details-save-schedule">${t("compose.save")}</button>
          </div>` : ""}
      </div>
      <div class="form-group"><label>${t("details.cadence")}</label>
        <p>Every ${data.cadence_weeks} week${data.cadence_weeks > 1 ? "s" : ""}</p>
        ${isFounder ? `<button class="btn-secondary" id="details-edit-cadence">${t("settings.edit")}</button>
          <div id="details-cadence-form" style="display:none;margin-top:.5rem">
            <input type="number" id="details-cadence-input" min="1" max="12" value="${data.cadence_weeks}">
            <button class="btn" id="details-save-cadence">${t("compose.save")}</button>
          </div>` : ""}
      </div>
      ${isFounder ? `
      <div class="form-group"><label>${t("details.name")}</label>
        <p>${escapeHtml(data.name)}</p>
        <button class="btn-secondary" id="details-edit-name">${t("settings.edit")}</button>
        <div id="details-name-form" style="display:none;margin-top:.5rem">
          <input type="text" id="details-name-input" value="${escapeHtml(data.name)}">
          <button class="btn" id="details-save-name">${t("compose.save")}</button>
        </div>
      </div>` : ""}
    </div>
    <h3 style="margin-top:2rem">${t("details.members")}</h3>
    <ul style="list-style:none;margin-top:.5rem">
      ${[...data.members].sort((a: any, b: any) => a.rotation_position - b.rotation_position)
        .map((m: any) => `
          <li style="padding:.5rem 0;border-bottom:1px solid var(--border);display:flex;justify-content:space-between;align-items:center">
            <span>${escapeHtml(m.display_name)} ${m.is_you ? "(you)" : ""} ${m.has_email ? "✉️" : ""}</span>
            <span style="display:flex;align-items:center;gap:.5rem">
              ${isFounder && !m.has_email ? `<button class="btn-secondary details-add-email" data-mid="${m.id}">${t("details.add_email")}</button>` : ""}
              <span style="color:var(--muted);font-size:.8rem">#${m.rotation_position + 1}</span>
            </span>
          </li>
          ${isFounder && !m.has_email ? `<li class="details-email-form" data-mid="${m.id}" style="display:none;padding:.5rem 0">
            <input type="email" class="details-email-input" placeholder="${t("email_ask.placeholder")}">
            <button class="btn details-save-email" data-mid="${m.id}">${t("compose.save")}</button>
          </li>` : ""}`).join("")}
    </ul>`;

  if (!isFounder) return;

  const toggle = (formId: string, btnId: string) => {
    view.querySelector(`#${btnId}`)?.addEventListener("click", () => {
      const f = view.querySelector<HTMLElement>(`#${formId}`)!;
      f.style.display = f.style.display === "none" ? "block" : "none";
    });
  };
  toggle("details-schedule-form", "details-edit-schedule");
  toggle("details-cadence-form", "details-edit-cadence");
  toggle("details-name-form", "details-edit-name");

  const dowSel = view.querySelector<HTMLSelectElement>("#details-dow");
  const hourSel = view.querySelector<HTMLSelectElement>("#details-hour");
  if (dowSel && hourSel) {
    const local = utcDowHourToLocal(data.publication_day_of_week, data.publication_hour_utc, offset);
    dowSel.value = String(local.dow);
    hourSel.value = String(local.hour);
  }

  view.querySelector("#details-save-schedule")?.addEventListener("click", async () => {
    const localDow = Number(dowSel!.value);
    const localHour = Number(hourSel!.value);
    // Half-hour timezones can't be stored exactly (see roundHalfToEven).
    // Say so before saving rather than silently redisplaying another hour.
    const landsOn = slotRoundsAway(localDow, localHour, offset);
    if (landsOn !== null) {
      const fmt = (h: number) => `${String(h).padStart(2, "0")}:00`;
      if (!confirm(t("settings.slot_rounded", { picked: fmt(localHour), actual: fmt(landsOn) }))) return;
    }
    const utc = localDowHourToUtc(localDow, localHour, offset);
    try {
      await apiFetch(`/groups/${groupId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          publication_day_of_week: utc.dow,
          publication_hour_utc: utc.hour,
        }),
      });
      toast(t("compose.saved"));
      await loadSettingsView({}, groupId);
    } catch (e: any) {
      toast(e.message || t("settings.delivery_save_failed"));
    }
  });

  view.querySelector("#details-save-cadence")?.addEventListener("click", async () => {
    const val = Number(view.querySelector<HTMLInputElement>("#details-cadence-input")!.value);
    try {
      await apiFetch(`/groups/${groupId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cadence_weeks: val }),
      });
      toast(t("compose.saved"));
      await loadSettingsView({}, groupId);
    } catch (e: any) {
      toast(e.message || t("settings.delivery_save_failed"));
    }
  });

  view.querySelector("#details-save-name")?.addEventListener("click", async () => {
    const val = view.querySelector<HTMLInputElement>("#details-name-input")!.value.trim();
    if (!val) return;
    try {
      await apiFetch(`/groups/${groupId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_name: val }),
      });
      toast(t("compose.saved"));
      await loadSettingsView({}, groupId);
    } catch (e: any) {
      toast(e.message || t("settings.delivery_save_failed"));
    }
  });

  view.querySelectorAll<HTMLButtonElement>(".details-add-email").forEach((btn) => {
    btn.addEventListener("click", () => {
      const mid = btn.dataset.mid;
      const form = view.querySelector<HTMLElement>(`.details-email-form[data-mid="${mid}"]`);
      if (form) form.style.display = "block";
    });
  });
  view.querySelectorAll<HTMLButtonElement>(".details-save-email").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const mid = btn.dataset.mid!;
      const input = view.querySelector<HTMLInputElement>(`.details-email-form[data-mid="${mid}"] .details-email-input`)!;
      try {
        await apiFetch(`/groups/${groupId}/members/${mid}/email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email_address: input.value.trim() }),
        });
        toast(t("compose.saved"));
        await loadSettingsView({}, groupId);
      } catch (e: any) {
        toast(e.message || t("settings.delivery_save_failed"));
      }
    });
  });
}
